/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useState } from 'react'
import { Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { SettingsSection } from '../components/settings-section'
import { useUpdateOption } from '../hooks/use-update-option'

type FlowTutorialSectionProps = {
  data: string
}

export function FlowTutorialSection({ data }: FlowTutorialSectionProps) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()
  const [content, setContent] = useState(data)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    setContent(data)
    setHasChanges(false)
  }, [data])

  const handleChange = (value: string) => {
    setContent(value)
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      await updateOption.mutateAsync({
        key: 'flow_tutorial_content',
        value: content,
      })
      setHasChanges(false)
      toast.success(t('Flow tutorial saved successfully'))
    } catch {
      toast.error(t('Failed to save Flow tutorial'))
    }
  }

  return (
    <SettingsSection
      title={t('Flow Tutorial')}
      description={t(
        'Markdown content displayed on the Flow page to guide users through setup'
      )}
    >
      <div className='space-y-4'>
        <Textarea
          value={content}
          onChange={(e) => handleChange(e.target.value)}
          rows={14}
          placeholder={t('Enter tutorial content (supports Markdown)')}
          className='font-mono text-sm'
        />
        <div className='flex justify-end'>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || updateOption.isPending}
            size='sm'
          >
            <Save className='mr-2 h-4 w-4' />
            {updateOption.isPending ? t('Saving...') : t('Save Tutorial')}
          </Button>
        </div>
      </div>
    </SettingsSection>
  )
}
